import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useBeforeunload from '../useBeforeunload';
import { AnalyzerTempTypes } from '../../types';
import {
  ANALYZER_ACTION,
  ANALYZER_ENTITY,
  ANALYZER_TIME_DELAY,
  CONTENT_KEY,
} from '../../commons/constants';
import { RootState } from '../../redux/store';
import { getSelectedAmenity } from '../../redux/selectors/projectItem.selector';
import { getProject } from '../../redux/selectors/project.selector';
import { getFocusFacility } from '../../redux/selectors/facility.selector';

type UseAnalyzerAmenityPropsTypes = {
  initial: boolean;
};

const useAnalyzerAmenity = (props: UseAnalyzerAmenityPropsTypes): void => {
  const { initial } = props;

  const [tempAmenity, setTempAmenity] = useState<AnalyzerTempTypes>();
  const [tempFacility, setTempFacility] = useState<AnalyzerTempTypes>();

  const contentActiveKey = useSelector((state: RootState) => state.projectModel.contentActiveKey);
  const isActivityWindow = useSelector((state: RootState) => state.layoutModel.isActivityWindow);
  const project = useSelector(getProject);
  const amenity = useSelector(getSelectedAmenity);
  const facility = useSelector(getFocusFacility);

  const amenityAnalyzer = (action: string, code: string, name: string) => {
    if (!project) return;

    if (window && window.ClikTracker && window.ClikTracker.sendCustomTrack) {
      window.ClikTracker.sendCustomTrack(action, ANALYZER_ENTITY.AMENITY, {
        project_code: project.code,
        project_name: project.name,
        amenity_code: code,
        amenity_name: name,
      });
    }
  };

  const facilityAnalyzer = (
    action: string,
    code: string,
    name: string,
    parentCode?: string,
    parentName?: string
  ) => {
    if (!project) return;

    if (window && window.ClikTracker && window.ClikTracker.sendCustomTrack) {
      window.ClikTracker.sendCustomTrack(action, ANALYZER_ENTITY.FACILITY, {
        project_code: project.code,
        project_name: project.name,
        amenity_code: tempAmenity ? tempAmenity.code : parentCode,
        amenity_name: tempAmenity ? tempAmenity.name : parentName,
        facility_code: code,
        facility_name: name,
      });
    }
  };

  // run event when close tab window or reload page
  useBeforeunload(async (event) => {
    if (!initial) return;

    const promiseAll = await Promise.all([
      new Promise((resolve) => {
        if (tempAmenity) {
          amenityAnalyzer(ANALYZER_ACTION.CLOSED, tempAmenity.code, tempAmenity.name);
        }

        resolve(true);
      }),
      new Promise((resolve) => {
        if (tempFacility) {
          facilityAnalyzer(ANALYZER_ACTION.CLOSED, tempFacility.code, tempFacility.name);
        }

        resolve(true);
      }),
    ]);

    // delay close tab for waiting send data to tracking module
    const start = Date.now();
    let now = start;
    while (now - start < ANALYZER_TIME_DELAY) {
      if (promiseAll.every((i) => i === true)) now = Date.now();
    }

    // this is needed to avoid to show a confirmation prompt
    delete event['returnValue'];
  });

  // check condition active window and current event data
  useEffect(() => {
    if (!initial || contentActiveKey !== CONTENT_KEY.AMENITIES.key) return;

    let action = ANALYZER_ACTION.INTERRUPTED;
    if (isActivityWindow) action = ANALYZER_ACTION.RESUMED;

    if (tempAmenity) {
      amenityAnalyzer(action, tempAmenity.code, tempAmenity.name);
    }

    if (tempFacility) {
      facilityAnalyzer(action, tempFacility.code, tempFacility.name);
    }
  }, [initial, isActivityWindow]);

  useEffect(() => {
    if (!initial || contentActiveKey !== CONTENT_KEY.AMENITIES.key) return;

    // check condition select new group
    if (amenity) {
      // selected new group and not store temp group
      if (!tempAmenity) {
        amenityAnalyzer(ANALYZER_ACTION.VIEWED, amenity.code, amenity.name);
      } else {
        amenityAnalyzer(ANALYZER_ACTION.CLOSED, tempAmenity.code, tempAmenity.name);
        amenityAnalyzer(ANALYZER_ACTION.VIEWED, amenity.code, amenity.name);
      }

      setTempAmenity({ code: amenity.code, name: amenity.name });
    } else if (tempAmenity) {
      amenityAnalyzer(ANALYZER_ACTION.CLOSED, tempAmenity.code, tempAmenity.name);
      setTempAmenity(undefined);
    }
  }, [initial, amenity]);

  // check condition focus item
  useEffect(() => {
    if (!initial || contentActiveKey !== CONTENT_KEY.AMENITIES.key || !tempAmenity) return;

    // check condition select new or remove item
    if (facility) {
      if (tempAmenity && tempAmenity.code !== amenity.code) {
        // close current item in case temp group and current selected group not same
        facilityAnalyzer(ANALYZER_ACTION.CLOSED, tempAmenity.code, tempAmenity.name);
      }

      // case change facility in amenity then close old facility
      if (
        tempAmenity &&
        tempAmenity.code === amenity?.code &&
        tempFacility &&
        tempFacility.code !== facility?.code
      ) {
        facilityAnalyzer(ANALYZER_ACTION.CLOSED, tempFacility.code, tempAmenity.name);
      }

      setTempFacility((prevState) => {
        if (!prevState || prevState.code !== facility.code) {
          return { code: facility.code, name: facility.name };
        }

        return prevState;
      });
    } else if (tempFacility) {
      // unselected item
      facilityAnalyzer(ANALYZER_ACTION.CLOSED, tempAmenity.code, tempAmenity.name);
      setTempFacility(undefined);
    }
  }, [initial, facility]);

  useEffect(() => {
    if (!tempFacility) return;

    facilityAnalyzer(ANALYZER_ACTION.VIEWED, tempFacility.code, tempFacility.name);
  }, [tempFacility]);
};

export default useAnalyzerAmenity;
